import FallbackIcon from '@atoms/FallbackIcon';
import { ReturnedAccount } from '@common/model/apiClient/Account';
import { docSummaryTypeConfig, DocumentStatus } from '@molecules/DocumentSummary/DocumentSummary';
import clsx from 'clsx';
import DevExpress from 'devextreme';
import { ContextMenu, Tooltip } from 'devextreme-react';
import { ContextMenuTypes } from 'devextreme-react/context-menu';
import Image from 'next/legacy/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import styles from './AccountCard.module.css';
import dxContextMenuItem = DevExpress.ui.dxContextMenu.Item;

type ContextMenuItemWithHandler = dxContextMenuItem & {
  onClick?: (accountId: number) => void;
};

interface AccountCardProps {
  account: ReturnedAccount;
  isCardView?: boolean;
}

function AccountCard({ account, isCardView }: AccountCardProps) {
  const router = useRouter();
  const intl = useIntl();

  const onContextClick = (e: ContextMenuTypes.ItemClickEvent, accountId: number) => {
    const itemData = e.itemData as ContextMenuItemWithHandler | undefined;
    if (!itemData?.onClick) return;
    return itemData.onClick(accountId);
  };

  const contextMenuItems: Array<ContextMenuItemWithHandler> = [
    {
      text: intl.formatMessage({
        defaultMessage: 'Open',
        description: 'Open link next to document on account page',
      }),
      onClick: (accountId: number) => {
        router.push(`/account/${accountId}`);
      },
    },
    {
      text: intl.formatMessage({
        defaultMessage: 'New Document',
        description: 'Document new document text',
      }),
      onClick: (accountId: number) => {
        router.push(`/account/${accountId}/document/new/`);
      },
    },
  ];

  return (
    <Link
      className={clsx(styles.account, { [styles.card]: isCardView })}
      key={account.id}
      id={`account${account.id}`}
      href={`/account/${account.id}`}
      onClick={() => {
        router.push('/account/' + account.id);
      }}
    >
      <div className={styles.imgPlate}>
        <div className={styles.imgWrapper}>
          {account.logo?.url ? (
            <Image alt={''} className={styles.img} src={account.logo.url as string} layout="fill" />
          ) : (
            <FallbackIcon isImage={true} resourceStatus={account.logo?.urlStatus} />
          )}
        </div>
      </div>
      <div className={styles.details}>
        <div className={styles.name}>{account.name}</div>
        <div className={styles.accountNumber}>{account.accountNumber ?? ' '}</div>
        {/*<div className={styles.description}>{account.description ?? ' '}</div>*/}
        <div className={styles.docs}>
          {[
            { documentStatus: DocumentStatus.ALL, count: account.totalCount ?? 0 },
            { documentStatus: DocumentStatus.CREATED, count: account.createdCount ?? 0 },
            { documentStatus: DocumentStatus.PROVIDED, count: account.providedCount ?? 0 },
            { documentStatus: DocumentStatus.ACCEPTED, count: account.acceptedCount ?? 0 },
            { documentStatus: DocumentStatus.REJECTED, count: account.rejectedCount ?? 0 },
          ].map((doc) => {
            const config = docSummaryTypeConfig[doc.documentStatus];
            // const ref = React.createRef<HTMLDivElement>();
            const id = `ds-${uuidv4().replace(/-/g, '')}`;
            return (
              <div id={id} key={doc.documentStatus} className={styles.summaryItem}>
                <span>{config.icon}</span>
                <span>{doc.count}</span>
                <Tooltip target={`#${id}`} showEvent={'dxhoverstart'} hideEvent={'dxhoverend'} contentRender={() => <div>{config.label}</div>} />
              </div>
            );
          })}
        </div>
      </div>
      {/*Display none wrapper to stop this from impacting layout*/}
      <span style={{ position: 'absolute' }}>
        <ContextMenu target={`#account${account.id}`} items={contextMenuItems} onItemClick={(e) => onContextClick(e, account.id)} />
      </span>
    </Link>
  );
}

export default AccountCard;
